import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Trash2, ChevronUp, ChevronDown, Map, Share2, Route, Loader2, Plus, Check, Calendar } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "../lib/api";
import { useSeo } from "../components/VIP";
import { TreasureMap } from "../components/TreasureMap";

const TYPE_LABEL = { venue: "Venue", event: "Event", attraction: "Attraction", activity: "Activity" };
const TYPE_COLOR = { venue: "bg-sky-500/20 text-sky-300", event: "bg-rose-500/20 text-rose-300", attraction: "bg-amber-500/20 text-amber-300", activity: "bg-emerald-500/20 text-emerald-300" };
const inp = "w-full bg-[#111] border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-rose-500";

const stopAddr = (s) => s.address || [s.city, s.state].filter(Boolean).join(", ");

export function mapsUrl(stops, origin, destination) {
  const names = [];
  if (origin) names.push(origin);
  stops.forEach((s) => names.push(stopAddr(s) || s.name));
  if (destination) names.push(destination);
  const pts = names.map((n) => encodeURIComponent(n)).filter(Boolean);
  if (pts.length < 2) return null;
  const waypoints = pts.slice(1, -1).join("|");
  return `https://www.google.com/maps/dir/?api=1&origin=${pts[0]}&destination=${pts[pts.length - 1]}${waypoints ? `&waypoints=${waypoints}` : ""}`;
}

function StopRow({ s, i, total, onMove, onRemove }) {
  return (
    <div className="flex items-center gap-3 bg-[#111] border border-white/10 rounded-xl p-3" data-testid={`trip-stop-${i}`}>
      <span className="font-display font-black text-lg text-amber-400 w-6 text-center shrink-0">{i + 1}</span>
      {s.image ? <img src={fileUrl(s.image)} alt="" className="w-11 h-11 rounded-lg object-cover shrink-0" /> : <div className="w-11 h-11 rounded-lg bg-white/5 shrink-0 flex items-center justify-center"><MapPin size={15} className="text-zinc-600" /></div>}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Link to={s.link} className="font-bold text-sm hover:text-rose-400 truncate">{s.name}</Link>
          <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${TYPE_COLOR[s.type]}`}>{TYPE_LABEL[s.type]}</span>
        </div>
        <p className="text-xs text-zinc-500 truncate">{stopAddr(s)}{s.event_date ? ` · ${s.event_date}` : ""}</p>
      </div>
      {onMove && (
        <div className="flex flex-col shrink-0">
          <button onClick={() => onMove(i, -1)} disabled={i === 0} className="text-zinc-500 hover:text-white disabled:opacity-20 p-0.5"><ChevronUp size={14} /></button>
          <button onClick={() => onMove(i, 1)} disabled={i === total - 1} className="text-zinc-500 hover:text-white disabled:opacity-20 p-0.5"><ChevronDown size={14} /></button>
        </div>
      )}
      {onRemove && <button onClick={() => onRemove(i)} className="text-zinc-500 hover:text-rose-400 p-1 shrink-0" data-testid={`trip-remove-${i}`}><Trash2 size={15} /></button>}
    </div>
  );
}

function ResultCard({ item, added, onToggle }) {
  const sub = item.venue_type || item.attraction_type || item.activity_type || (item.event_date ? item.event_date : "");
  return (
    <div className={`flex items-center gap-3 border rounded-xl p-3 transition-colors ${added ? "border-amber-400/50 bg-amber-400/5" : "border-white/10 bg-[#111]"}`} data-testid={`route-item-${item.id}`}>
      {item.image ? <img src={fileUrl(item.image)} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" /> : <div className="w-12 h-12 rounded-lg bg-white/5 shrink-0 flex items-center justify-center"><MapPin size={16} className="text-zinc-600" /></div>}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={item.link} className="font-bold text-sm hover:text-rose-400 truncate">{item.name}</Link>
          <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${TYPE_COLOR[item.type]}`}>{TYPE_LABEL[item.type]}</span>
          {item.vip && <span className="text-[9px] font-extrabold uppercase bg-amber-400 text-black px-1.5 py-0.5 rounded-full shrink-0">VIP</span>}
        </div>
        <p className="text-xs text-zinc-500 truncate">{[item.city, item.state].filter(Boolean).join(", ")}{sub ? ` · ${sub}` : ""}{item.off_route_miles > 3 ? ` · ~${Math.round(item.off_route_miles)} mi off route` : " · on your route"}</p>
      </div>
      <button onClick={onToggle} className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-bold ${added ? "bg-amber-400 text-black" : "bg-rose-500 hover:bg-rose-600"}`} data-testid={`route-add-${item.id}`}>
        {added ? <Check size={16} /> : <Plus size={16} />}
      </button>
    </div>
  );
}

function PlaceInput({ label, color, placeholder, value, onChange, testid }) {
  const [q, setQ] = useState(value?.label || "");
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open || q.trim().length < 3) { setOptions([]); return; }
    const t = setTimeout(() => {
      api.get(`/geocode/search?q=${encodeURIComponent(q.trim())}`)
        .then((r) => setOptions(r.data)).catch(() => setOptions([]));
    }, 350);
    return () => clearTimeout(t);
  }, [q, open]);
  const pick = (o) => { onChange(o); setQ(o.label); setOpen(false); setOptions([]); };
  return (
    <div className="relative">
      <label className={`text-[11px] uppercase tracking-wider font-bold ${color}`}>{label}</label>
      <input className={`${inp} mt-1`} placeholder={placeholder} value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); onChange({ label: e.target.value }); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 200)}
        data-testid={testid} autoComplete="off" />
      {open && options.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-[#161616] border border-white/15 rounded-xl overflow-hidden shadow-2xl" data-testid={`${testid}-options`}>
          {options.map((o, i) => (
            <button type="button" key={`${o.lat}-${o.lon}-${i}`} onMouseDown={() => pick(o)}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-white/5 flex items-center gap-2" data-testid={`${testid}-option-${i}`}>
              <MapPin size={13} className="text-zinc-500 shrink-0" />
              <span className="truncate">{o.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const TYPES = ["venue", "event", "attraction", "activity"];

export default function PlanTrip() {
  useSeo("Plan My Trip | Gulf Coast VIP", "Point A to Point B — discover venues, events, attractions and activities along your Gulf Coast route and build a treasure-map itinerary.");
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [date, setDate] = useState("");
  const [types, setTypes] = useState(TYPES);
  const [searching, setSearching] = useState(false);
  const [route, setRoute] = useState(null);
  const [name, setName] = useState("My Gulf Coast Trip");
  const [stops, setStops] = useState(() => {
    try { return JSON.parse(localStorage.getItem("gcv_trip2") || "[]"); } catch { return []; }
  });
  useEffect(() => { localStorage.setItem("gcv_trip2", JSON.stringify(stops)); }, [stops]);

  const toggleType = (t) => setTypes((cur) => cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]);

  const findStops = async (e) => {
    e?.preventDefault();
    if (!from?.label?.trim() || !to?.label?.trim()) { toast.error("Enter both a starting point and a destination"); return; }
    setSearching(true);
    try {
      const p = new URLSearchParams({ from_city: from.label.trim(), to_city: to.label.trim(), types: types.join(",") });
      if (from.lat != null) { p.set("from_lat", from.lat); p.set("from_lon", from.lon); }
      if (to.lat != null) { p.set("to_lat", to.lat); p.set("to_lon", to.lon); }
      if (date) p.set("date", date);
      const { data } = await api.get(`/trips/route-stops?${p}`);
      setRoute(data);
      if (!data.items.length) toast.info("No listings found along that route yet — try widening your dates or types");
    } catch (err) { toast.error(errMsg(err)); } finally { setSearching(false); }
  };

  const toggleStop = (item) => setStops((cur) => {
    if (cur.some((s) => s.id === item.id)) return cur.filter((s) => s.id !== item.id);
    return [...cur, { type: item.type, id: item.id, name: item.name, slug: item.slug, image: item.image, city: item.city, state: item.state, lat: item.lat, lon: item.lon, event_date: item.event_date, link: item.link }];
  });
  const move = (i, dir) => setStops((s) => {
    const j = i + dir; if (j < 0 || j >= s.length) return s;
    const next = [...s]; [next[i], next[j]] = [next[j], next[i]]; return next;
  });
  const remove = (i) => setStops((s) => s.filter((_, idx) => idx !== i));

  const originLabel = route?.origin?.label || from?.label || "";
  const destLabel = route?.destination?.label || to?.label || "";
  const gmaps = mapsUrl(stops, originLabel, destLabel);

  const share = async () => {
    try {
      const { data } = await api.post("/trips", {
        name, date, from_label: originLabel, to_label: destLabel,
        from_lat: route?.origin?.lat ?? from?.lat, from_lon: route?.origin?.lon ?? from?.lon,
        to_lat: route?.destination?.lat ?? to?.lat, to_lon: route?.destination?.lon ?? to?.lon,
        stops: stops.map((s) => ({ type: s.type, id: s.id })),
      });
      const url = `${window.location.origin}/trip/${data.id}`;
      if (navigator.share) { await navigator.share({ title: name, url }); }
      else { await navigator.clipboard.writeText(url); toast.success("Share link copied to clipboard"); }
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="px-6 md:px-12 lg:px-20 py-12 min-h-[60vh]" data-testid="plan-trip-page">
      <p className="overline mb-2">Route planner</p>
      <h1 className="font-display font-black text-4xl sm:text-5xl mb-3 flex items-center gap-3"><Route className="text-rose-400" size={38} /> Plan My Trip</h1>
      <p className="text-zinc-400 max-w-2xl mb-8">Tell us where you're headed — we'll show you every venue, event, attraction and activity along the way. Pick your stops and get your treasure map.</p>

      {/* Step 1: route form */}
      <form onSubmit={findStops} className="bg-[#0e0e0e] border border-white/10 rounded-2xl p-5 md:p-6 mb-8" data-testid="route-form">
        <div className="grid md:grid-cols-[1fr_1fr_180px] gap-3">
          <PlaceInput label="From — Point A" color="text-emerald-300" placeholder="Address, place or city — e.g. 118 Palafox Pl, Pensacola"
            value={from} onChange={setFrom} testid="trip-from" />
          <PlaceInput label="To — Point B" color="text-rose-300" placeholder="Address, place or city — e.g. Gulf Shores, AL"
            value={to} onChange={setTo} testid="trip-to" />
          <div>
            <label className="text-[11px] uppercase tracking-wider text-zinc-400 font-bold flex items-center gap-1"><Calendar size={11} /> When (optional)</label>
            <input type="date" className={`${inp} mt-1`} value={date} onChange={(e) => setDate(e.target.value)} data-testid="trip-date" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <span className="text-xs text-zinc-500 mr-1">Show me:</span>
          {TYPES.map((t) => (
            <button type="button" key={t} onClick={() => toggleType(t)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors ${types.includes(t) ? "bg-rose-500 border-rose-500" : "border-white/15 text-zinc-400 hover:text-white"}`}
              data-testid={`type-toggle-${t}`}>{TYPE_LABEL[t]}s</button>
          ))}
          <button type="submit" disabled={searching} className="ml-auto bg-amber-400 hover:bg-amber-300 text-black font-bold text-sm px-6 py-2.5 rounded-full flex items-center gap-2 disabled:opacity-50" data-testid="find-route-btn">
            {searching ? <Loader2 size={15} className="animate-spin" /> : <Map size={15} />} Find stops on my route
          </button>
        </div>
      </form>

      <div className="grid lg:grid-cols-2 gap-8 items-start">
        {/* Step 2: results */}
        <div>
          <h2 className="font-display font-bold text-xl mb-3">
            {route ? <>On your route <span className="text-zinc-500 text-sm font-normal">({route.items.length} found · ~{route.distance_miles} mi{route.date ? ` · ${route.date}` : ""})</span></> : "Stops along the way"}
          </h2>
          {!route && <p className="text-zinc-500 text-sm py-6">Enter your route above and hit <b className="text-amber-300">Find stops</b> — everything between Point A and Point B shows up here, in driving order.</p>}
          {route && (
            <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1" data-testid="route-results">
              {route.items.map((item) => (
                <ResultCard key={item.id} item={item} added={stops.some((s) => s.id === item.id)} onToggle={() => toggleStop(item)} />
              ))}
              {route.items.length === 0 && <p className="text-zinc-500 text-sm py-6" data-testid="route-empty">Nothing on this route yet — try different types or no date filter.</p>}
            </div>
          )}
        </div>

        {/* Step 3: itinerary */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-xl">Your trip <span className="text-zinc-500 text-sm font-normal">({stops.length} stop{stops.length === 1 ? "" : "s"})</span></h2>
            {stops.length > 0 && <button onClick={() => setStops([])} className="text-xs text-zinc-500 hover:text-rose-400" data-testid="trip-clear">Clear all</button>}
          </div>
          <input className={`${inp} mb-3`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Name your adventure..." data-testid="trip-name" />
          <div className="space-y-2" data-testid="trip-stops">
            {stops.map((s, i) => <StopRow key={s.id} s={s} i={i} total={stops.length} onMove={move} onRemove={remove} />)}
          </div>
          {stops.length === 0 && <p className="text-zinc-500 text-sm py-4">Tap <b className="text-rose-400">+</b> on a stop to add it to your trip.</p>}

          {stops.length >= 1 && (
            <>
              <div className="mt-6">
                <TreasureMap title={name} stops={stops}
                  origin={route?.origin || from}
                  destination={route?.destination || to} />
              </div>
              <div className="flex flex-wrap gap-3 mt-4">
                {gmaps && <a href={gmaps} target="_blank" rel="noreferrer" className="bg-rose-500 hover:bg-rose-600 font-bold text-sm px-5 py-2.5 rounded-full flex items-center gap-2" data-testid="trip-maps-btn"><Map size={15} /> Open route in Google Maps</a>}
                <button onClick={share} className="border border-amber-400/50 text-amber-300 hover:bg-amber-400/10 font-bold text-sm px-5 py-2.5 rounded-full flex items-center gap-2" data-testid="trip-share-btn"><Share2 size={15} /> Save & share map</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function TripView() {
  const { id } = useParams();
  const [trip, setTrip] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/trips/${id}`).then((r) => setTrip(r.data)).catch(() => setErr(true)); }, [id]);
  useSeo(trip ? `${trip.name} | Gulf Coast VIP` : "Trip | Gulf Coast VIP");
  if (err) return <div className="px-6 py-24 text-center text-zinc-500">Trip not found.</div>;
  if (!trip) return <div className="px-6 py-24 text-center text-zinc-500">Loading trip…</div>;
  const origin = trip.route?.from, dest = trip.route?.to;
  const gmaps = mapsUrl(trip.stops, origin?.label, dest?.label);
  return (
    <div className="px-6 md:px-12 lg:px-20 py-12 max-w-4xl mx-auto" data-testid="trip-view-page">
      <p className="overline mb-2">Shared itinerary{trip.date ? ` · ${trip.date}` : ""}</p>
      <h1 className="font-display font-black text-3xl sm:text-4xl mb-6">{trip.name}</h1>
      <TreasureMap title={trip.name} origin={origin} destination={dest} stops={trip.stops} />
      <div className="space-y-2 mt-6">
        {trip.stops.map((s, i) => <StopRow key={s.id} s={s} i={i} total={trip.stops.length} />)}
      </div>
      <div className="flex flex-wrap gap-3 mt-6">
        {gmaps && <a href={gmaps} target="_blank" rel="noreferrer" className="bg-rose-500 hover:bg-rose-600 font-bold text-sm px-5 py-2.5 rounded-full flex items-center gap-2" data-testid="trip-view-maps-btn"><Map size={15} /> Open route in Google Maps</a>}
        <Link to="/plan-trip" className="border border-white/15 hover:border-white/40 font-bold text-sm px-5 py-2.5 rounded-full">Plan your own trip</Link>
      </div>
    </div>
  );
}
